import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ProjectCard from "./ProjectCard";
import { RevealText } from "./common/RevealText";
import Container from "./common/Container";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllProjects } from "../redux/slice/project.slice";
import { getAllCategories } from "../redux/slice/category.slice";

/* ─────────────────────────────────────────────
   Small-screen auto-play touch slider
   Uses CSS translateX on a full-width track —
   no AnimatePresence so cards are never unmounted
   and there's no height-collapse bug.
───────────────────────────────────────────── */
const MobileSlider = ({ projects }) => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const dragStartX = useRef(0);
  const autoRef = useRef(null);
  const total = projects.length;

  const go = useCallback(
    (next) => {
      if (animating) return;
      setAnimating(true);
      setCurrent((next + total) % total);
      setTimeout(() => setAnimating(false), 500);
    },
    [animating, total]
  );

  // Auto-advance every 3.5 s
  useEffect(() => {
    autoRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 3500);
    return () => clearInterval(autoRef.current);
  }, [total]);

  const pauseAuto = () => clearInterval(autoRef.current);
  const resumeAuto = () => {
    clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 3500);
  };

  /* Touch / drag */
  const onTouchStart = (e) => {
    pauseAuto();
    dragStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    const diff = dragStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      go(diff > 0 ? current + 1 : current - 1);
    }
    resumeAuto();
  };

  return (
    <div className="relative w-full">
      {/* ── Track: all cards laid out side-by-side, shifted with translateX ── */}
      <div
        className="w-full overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="flex"
          style={{
            transform: `translateX(-${current * 100}%)`,
            transition: "transform 0.5s cubic-bezier(0.32, 0.72, 0, 1)",
            willChange: "transform",
          }}
        >
          {projects.map((project, index) => (
            <div
              key={project.id}
              className="w-full flex-shrink-0 px-4"
              style={{ minWidth: "100%" }}
            >
              {/* Inline card — full control, no dependency on ProjectCard internals */}
              <div
                className="relative w-full rounded-sm overflow-hidden cursor-pointer group"
                style={{ height: 420 }}
                onClick={() => window.location.href = project.link}
              >
                {/* Image */}
                <img
                  src={project.image}
                  alt={project.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                {/* Top badge */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
                  <span className="text-secondary text-[10px] font-bold tracking-[0.2em] uppercase">
                    {project.category}
                  </span>
                  <span className="text-white/20 font-serif text-4xl font-bold leading-none">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* Bottom content */}
                <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-[1px] w-6 bg-secondary/60" />
                    <span className="text-secondary/80 text-[10px] tracking-widest uppercase">
                      {project.status}
                    </span>
                  </div>
                  <h3 className="text-white font-serif text-2xl leading-tight mb-2">
                    {project.title}
                  </h3>
                  {project.description && (
                    <p className="text-white/60 text-xs leading-relaxed line-clamp-2 mb-4">
                      {project.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-secondary text-[10px] font-bold tracking-[0.15em] uppercase">
                    Explore Project
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Pill progress bar ── */}
      <div className="flex justify-center items-center overflow-hidden mt-5 space-x-1">
        {projects.map((_, i) => (
          <div
            key={i}
            onClick={() => { pauseAuto(); go(i); resumeAuto(); }}
            className="relative h-[5px] rounded-full overflow-hidden transition-all duration-500"
            style={{
              width: i === current ? 36 : 10,
              background: "rgba(198,168,124,0.25)",
            }}
          >
            {i === current && (
              <motion.span
                key={current}
                className="absolute inset-0 w-full h-[85%] my-auto bg-secondary rounded-full origin-left ml-[0.5px]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 3.5, ease: "linear" }}
              />
            )}
          </div>
        ))}
      </div>

      {/* ── Counter ── */}
      <div className="flex justify-end px-6 mt-3 text-xs font-mono text-secondary/60 select-none">
        {String(current + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </div>

      {/* ── Prev / Next arrows ── */}
      <button
        onClick={() => { pauseAuto(); go(current - 1); resumeAuto(); }}
        className="absolute left-6 top-[35%] -translate-y-1/2 w-9 h-9 rounded-full border border-white/20 bg-primary/80 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-secondary hover:border-secondary transition-colors z-20"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={() => { pauseAuto(); go(current + 1); resumeAuto(); }}
        className="absolute right-6 top-[35%] -translate-y-1/2 w-9 h-9 rounded-full border border-white/20 bg-primary/80 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-secondary hover:border-secondary transition-colors z-20"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main Projects section
───────────────────────────────────────────── */
const Projects = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const { projects: rawProjects = [] } = useSelector((state) => state.project);
  const { categories } = useSelector((state) => state.category);

  useEffect(() => {
    if (rawProjects.length === 0) dispatch(getAllProjects());
    if (categories.length === 0) dispatch(getAllCategories());
  }, [dispatch]);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const projects = (rawProjects || []).slice().reverse().map((p) => {
    const categoryName =
      categories.find((c) => c._id === p.category)?.name || "Uncategorized";
    return {
      id: p._id,
      title: p.title,
      status: p.status || "Ongoing",
      category: categoryName,
      image: p.images?.[0]?.url || "https://via.placeholder.com/800x600",
      link: `/project/${p.sku}`,
      description: p.description,
    };
  });

  /* ── Desktop: scroll-based horizontal track ── */
  useEffect(() => {
    if (isMobile) return;

    let rafId;
    let customScrollContainer = null;

    const handleScroll = () => {
      if (!sectionRef.current) return;
      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;
      const viewportHeight = window.innerHeight;
      const scrollableHeight = sectionHeight - viewportHeight;
      const scrolled = -rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / scrollableHeight));
      setScrollProgress(progress);
    };

    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(handleScroll);
    };

    const findScrollContainer = () => {
      if (!sectionRef.current) return null;
      let element = sectionRef.current.parentElement;
      while (element) {
        const style = window.getComputedStyle(element);
        if (style.overflowY === "scroll" || style.overflowY === "auto") return element;
        element = element.parentElement;
      }
      return null;
    };

    handleScroll();
    customScrollContainer = findScrollContainer();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    if (customScrollContainer)
      customScrollContainer.addEventListener("scroll", onScroll, { passive: true });

    const intervalId = setInterval(handleScroll, 100);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", handleScroll);
      document.removeEventListener("scroll", onScroll, { capture: true });
      if (customScrollContainer) customScrollContainer.removeEventListener("scroll", onScroll);
      clearInterval(intervalId);
    };
  }, [isMobile]);

  const [scrollRange, setScrollRange] = useState(0);

  useEffect(() => {
    if (isMobile || !trackRef.current) return;

    const updateDimensions = () => {
      if (trackRef.current) {
        const totalWidth = trackRef.current.scrollWidth;
        const visibleWidth = window.innerWidth;
        setScrollRange(Math.max(0, totalWidth - visibleWidth + 100));
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [projects, isMobile]);

  const translateX = `-${scrollProgress * scrollRange}px`;

  // Header and Footer are plain JSX references — NOT inline components.
  // Defining them as components inside the render fn causes remounts every scroll tick,
  // which re-triggers whileInView and causes blinking.
  const headerJSX = (
    <Container className="w-full relative z-10 mb-8 md:mb-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end border-b border-primary/10 pb-8">
        <div className="md:col-span-8">
          {/* Plain div — animate once via CSS, no whileInView */}
          <div style={{ opacity: 1 }}>
            <div className="flex items-center gap-4 mb-4">
              <span className="h-[1px] w-12 bg-secondary/50" />
              <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs">
                Selected Works
              </span>
            </div>
            {/* <h2 className="text-3xl md:text-5xl lg:text-6xl font-serif text-primary leading-none tracking-tight">
              <span className="block">Architectural</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary italic block mt-2">
                Vision
              </span>
            </h2> */}
             <h2 className="text-3xl md:text-4xl lg:text-5xl  font-serif text-primary font-bold leading-tight">
                                <RevealText text="Architectural" className='leading-tight' />
                                <span className=" block mt-2 leading-tight">Vision</span>
            </h2>
          </div>
        </div>
        <div className="md:col-span-4 md:mb-2">
          <p className="text-secondary text-sm  md:text-base leading-relaxed border-l border-primary/10 pl-6">
            Our portfolio represents a dialogue between space, nature, and human experience. Each
            project is a testament to sustainable luxury and thoughtful design.
          </p>
        </div>
      </div>
    </Container>
  );

  const footerJSX = (
    <Container className="w-full">
      <div className="flex justify-center mt-12 md:mt-16">
        <button
          onClick={() => navigate("/projects")}
          className="group relative px-12 py-4 bg-transparent overflow-hidden text-secondary font-bold tracking-[0.2em] text-xs uppercase border border-secondary hover:text-white hover:border-transparent transition-colors duration-500"
        >
          <span className="relative z-10 flex items-center gap-3">
            View Full Portfolio
            <svg
              className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </span>
          <div className="absolute inset-0 bg-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left ease-out" />
        </button>
      </div>
    </Container>
  );

  /* ── MOBILE layout (no sticky scroll magic) ── */
  if (isMobile) {
    return (
      <section className="relative bg-background py-16 px-0">
        {/* Background dots */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle at 2px 2px, #3E362E 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        {/* Background grid */}
        <div
          className="absolute inset-0 z-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #C6A87C 1px, transparent 1px), linear-gradient(to bottom, #C6A87C 1px, transparent 1px)",
            backgroundSize: "100px 100px",
          }}
        />

        <div className="relative z-10">
          {headerJSX}
          <MobileSlider projects={projects} />
          <div className="mt-8">
            {footerJSX}
          </div>
        </div>
      </section>
    );
  }

  /* ── DESKTOP layout (original scroll-based) ── */
  return (
    <section ref={sectionRef} className="relative bg-background" style={{ height: "400vh" }}>
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden py-12">
        {/* Background patterns */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle at 2px 2px, #3E362E 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        <div
          className="absolute inset-0 z-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #C6A87C 1px, transparent 1px), linear-gradient(to bottom, #C6A87C 1px, transparent 1px)",
            backgroundSize: "100px 100px",
          }}
        />

        {headerJSX}

        {/* Horizontal scroll track */}
        <div className="w-full overflow-hidden px-4 md:px-6 perspective-1000 relative z-10 h-[65vh] sm:h-[550px] md:h-[650px] xl:h-[800px] flex items-center">
          <motion.div
            ref={trackRef}
            className="flex gap-8 md:gap-16 items-center will-change-transform pl-[10vw]"
            style={{
              transform: `translateX(${translateX})`,
              transition: "transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
            }}
          >
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                className="w-[90vw] sm:w-[350px] md:w-[450px] lg:w-[550px] xl:w-[650px] flex-shrink-0 relative shadow-[0_0_40px_rgba(0,0,0,0.08)]"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-5%" }}
                transition={{ duration: 0.8, delay: index * 0.1, type: "spring", stiffness: 50 }}
              >
                <div className="h-[55vh] sm:h-[450px] md:h-[550px] xl:h-[700px] relative z-10">
                  <ProjectCard {...project} index={index} />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {footerJSX}

        {/* Scroll progress debug pill */}
        <div className="fixed bottom-4 right-4 bg-tertiary border border-secondary/20 text-white px-4 py-2 rounded-lg shadow-lg z-50 opacity-0 hover:opacity-100 transition-opacity">
          <div className="text-xs font-mono text-secondary">
            Scroll: {(scrollProgress * 100).toFixed(0)}%
          </div>
          <div className="w-32 h-2 bg-primary rounded mt-1 border border-white/10">
            <div
              className="h-full bg-secondary rounded transition-all duration-100"
              style={{ width: `${scrollProgress * 100}%` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;